import React, { useEffect, useMemo, useState } from 'react';

import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { ReasonToCancelTask as ReasonToCancelTaskType, Task } from '@bringg/types';
import { Select, Option, Modal, BringgTextArea, Notification, Spinner, Button } from '@bringg/react-components';
import { filterReasonsByOrderType } from '@bringg/common-utils';
import { useTranslation } from 'react-i18next';

import { useHasFeatureFlag } from '../../../utils/feature-flags';

import styles from './reason-to-cancel-task-modal.module.scss';

interface Props {
	onCancelTasks: () => void;
	tasks: Task[];
	onModalClose: () => void;
}

const ReasonToCancelTask: React.FC<Props> = ({ onCancelTasks, tasks, onModalClose }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [cancelReasons, setCancelReasons] = useState<ReasonToCancelTaskType[]>([]);
	const [reasonText, setReasonText] = useState<string>();
	const [selectedReason, setSelectedReason] = useState<number>();
	const [isVisible, setIsVisible] = useState(false);
	const [isOkDisabled, setIsOkDisabled] = useState(false);

	const { t } = useTranslation();

	const filterCancelReasonsByTaskType = useHasFeatureFlag('filter_cancel_reasons_by_task_type');

	const getTasksValue = (value: string) => {
		const taskValue = tasks.map(task => task[value]);
		return new Set(taskValue);
	};

	const onErrorOnSelectedTasks = (error: string) => {
		Notification.error(error);
		onModalClose();
		setIsLoading(false);
	};

	const getTasksCancelReason = async () => {
		setIsLoading(true);
		const uniqueTagIds = getTasksValue('tag_id');
		if (uniqueTagIds.size > 1) {
			onErrorOnSelectedTasks(t('CANCEL_TASK_CONTROLLER.THESE_ORDERS_BELONG_TO_MORE_THAN_ONE_TAG'));
			return;
		}
		let reasons: ReasonToCancelTaskType[];

		try {
			reasons = await getRootEnv().dashboardSdk.sdk.reasonToCancelTasks.getAll(Array.from(uniqueTagIds)[0]);
		} catch (e) {
			Notification.error(t('ORDER.ERROR'), t('ORDER.FAILED_TO_CANCEL_ORDER'));
			console.error('Failed to fetch cancel reason', e);
			onModalClose();
		}

		if (filterCancelReasonsByTaskType) {
			const uniqueTaskTypeIds = getTasksValue('task_type_id');
			if (uniqueTaskTypeIds.size > 1) {
				onErrorOnSelectedTasks(t('CANCEL_TASK_CONTROLLER.ORDERS_BELONG_TO_MORE_THAN_ONE_TASK_TYPE'));
				return;
			}
			reasons = filterReasonsByOrderType(reasons, Array.from(uniqueTaskTypeIds)[0]);
		}
		setIsVisible(true);
		setIsLoading(false);
		setCancelReasons(reasons);
	};

	useEffect(() => {
		getTasksCancelReason();
	}, []);

	const cancelTask = async () => {
		try {
			const taskIds = tasks.map(task => task.id);
			setIsOkDisabled(true);
			await getRootEnv().dashboardSdk.sdk.v2.tasks.massCancel(taskIds, selectedReason, reasonText);
			onCancelTasks();
		} catch (e) {
			Notification.error(t('ORDER.ERROR'), t('ORDER.FAILED_TO_CANCEL_ORDER'));
			console.error('Cancel task failed', e);
			setIsOkDisabled(false);
		}
	};

	const reasonOptions = useMemo(
		() =>
			cancelReasons.map(reasonToCancelTask => (
				<Option key={reasonToCancelTask.id} value={reasonToCancelTask.id}>
					<span role="option" aria-selected="false">
						{reasonToCancelTask.reason}
					</span>
				</Option>
			)),
		[cancelReasons]
	);

	const modalFooter = useMemo(() => {
		return (
			<div className="reason-to-cancel-task-buttons-container">
				<Button type="link" onClick={onModalClose}>
					{t('ORDER_INVENTORY.CANCEL')}
				</Button>
				<Button disabled={!selectedReason} onClick={cancelTask} type="primary" loading={isOkDisabled}>
					{t('ORDER_INVENTORY.SAVE')}
				</Button>
			</div>
		);
	}, [isOkDisabled, selectedReason]);

	return (
		<Modal
			visible={isVisible}
			title={t('CANCEL_ORDER_REASON.CANCEL_ORDER')}
			onCancel={onModalClose}
			closable={false}
			maskClosable={false}
			className={styles.cancelReasonModal}
			footer={modalFooter}
		>
			{isLoading ? (
				<Spinner />
			) : (
				<>
					<div className={styles.cancelReason}>
						<span className={styles.inputTitle}>{t('CANCEL_ORDER_REASON.CANCELLATION_REASON')}</span>
						<Select
							onChange={setSelectedReason}
							className={styles.cancelReasonDropdown}
							data-test-id="cancel-reason"
						>
							{reasonOptions}
						</Select>
					</div>
					<div>
						<span className={styles.inputTitle}>{t('CANCEL_ORDER_REASON.ADDITIONAL_TEXT')}</span>
						<BringgTextArea
							value={reasonText}
							onChange={e => setReasonText(e.target.value)}
							rows={5}
							data-test-id="cancel-description"
						/>
					</div>
				</>
			)}
		</Modal>
	);
};

export default ReasonToCancelTask;
